import { motion } from "framer-motion";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { CHAUFFEUR_HUB } from "./hubDict";

type LocationsSectionProps = { locale?: Locale };

const LocationsSection = ({
  locale = "fr",
}: LocationsSectionProps): JSX.Element => {
  const d = tr(CHAUFFEUR_HUB, locale).locations;
  const items = d.items;
  const seoLine = items.map((i) => i.name).join(", ");
  return (
    <section className="relative overflow-hidden bg-white py-16 text-black">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-black/[0.02] to-transparent" />
      <div className="pointer-events-none absolute -left-20 top-8 h-44 w-44 rounded-full bg-primary/20 blur-[90px]" />
      <div className="relative mx-auto max-w-[90rem] px-6 md:px-8">
        <motion.p
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.4 }}
          className="mb-2 text-xs uppercase tracking-[0.18em] text-black/55"
        >
          {d.eyebrow}
        </motion.p>
        <motion.h2
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.45 }}
          className="mb-2 font-space-age text-xl tracking-[0.2em]"
        >
          {d.heading}
        </motion.h2>
        <motion.p
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.45, delay: 0.05 }}
          className="mb-8 max-w-3xl text-sm text-black/70"
        >
          {d.introPrefix}
          {seoLine}.
        </motion.p>
        <p className="sr-only">
          {d.srPrefix}
          {seoLine}
        </p>
        <div className="grid items-start gap-5 lg:grid-cols-12">
          <div className="self-start rounded-3xl border border-black/10 bg-gradient-to-br from-black/[0.03] to-transparent p-6 lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.2em] text-gold-800">
              {d.corridorsTitle}
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {items.slice(0, 6).map((loc, idx) => (
                <motion.div
                  key={loc.name}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.35, delay: idx * 0.04 }}
                  whileHover={{ y: -2 }}
                  className="rounded-xl border border-black/10 bg-white px-4 py-3 transition hover:-translate-y-0.5 hover:border-gold-500/40"
                >
                  <p className="text-sm font-semibold">{`${d.corridorPrefix}${loc.name}`}</p>
                  <p className="mt-1 text-xs text-black/65">{d.corridorNote}</p>
                </motion.div>
              ))}
            </div>
          </div>
          <div className="space-y-3 self-start lg:col-span-5">
            {items.map((loc, idx) => (
              <motion.div
                key={loc.name}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.35, delay: idx * 0.03 }}
                whileHover={{ x: 2 }}
                className="group rounded-2xl border border-black/10 bg-black/[0.02] p-5 transition hover:-translate-y-0.5 hover:border-gold-500/35 hover:bg-black/[0.04]"
              >
                <h3 className="text-base font-semibold">{loc.name}</h3>
                {loc.description && (
                  <p className="mt-1 text-sm text-black/75">
                    {loc.description}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationsSection;
